/**
 * useUserCalendars — 내 캘린더 목록(기본 + 내가 만든 것) + 표시 켜기/끄기 설정 (domain/calendar.ts, 2026-10-29).
 *
 * - 표시 설정은 서버 문서(calendar_prefs/state) — 기기 간 공유. 끈 항목 키만 저장하므로 새 캘린더는 기본 켜짐.
 * - rules 미배포(permission-denied)면 mode "unavailable": 기본 캘린더만 보이고 추가·설정은 이 창 메모리에서만(저장 안 됨).
 * - 실시간 목록이라 orderBy 없이 domain sortCalendars.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { userCalendarsRepo } from "@/services/userCalendars.repository";
import {
  DEFAULT_CALENDAR,
  DEFAULT_CALENDAR_ID,
  USER_CALENDARS_MAX,
  nextCalendarColor,
  normalizeCalendarName,
  sortCalendars,
  type CalendarColorKey,
  type UserCalendar,
} from "@/domain/calendar";

export type UserCalendarsMode = "loading" | "remote" | "unavailable" | "error";

const isDenied = (e: unknown) => !!e && typeof e === "object" && (e as { code?: unknown }).code === "permission-denied";

export function useUserCalendars() {
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const [remote, setRemote] = useState<{ uid: string; mode: Exclude<UserCalendarsMode, "loading">; list: UserCalendar[] } | null>(null);
  const [hiddenState, setHiddenState] = useState<{ uid: string; set: Set<string> } | null>(null);

  useEffect(() => {
    if (!uid) return;
    let alive = true;
    const unsub = userCalendarsRepo.watch(
      uid,
      (list) => { if (alive) setRemote({ uid, mode: "remote", list }); },
      (e) => { if (alive) setRemote({ uid, mode: isDenied(e) ? "unavailable" : "error", list: [] }); },
    );
    const unsubH = userCalendarsRepo.watchHidden(
      uid,
      (set) => { if (alive) setHiddenState({ uid, set }); },
      () => { if (alive) setHiddenState({ uid, set: new Set() }); },
    );
    return () => { alive = false; unsub(); unsubH(); };
  }, [uid]);

  const cur = remote && remote.uid === uid ? remote : null;
  const mode: UserCalendarsMode = cur ? cur.mode : "loading";
  const own = useMemo(() => sortCalendars(cur?.list ?? []), [cur]);
  /** 기본 캘린더 포함 전체 */
  const calendars = useMemo(() => [DEFAULT_CALENDAR, ...own], [own]);
  const calendarOf = useCallback(
    (id: string | undefined): UserCalendar => (id && calendars.find((c) => c.id === id)) || DEFAULT_CALENDAR,
    [calendars],
  );

  const hidden = useMemo(() => (hiddenState && hiddenState.uid === uid ? hiddenState.set : new Set<string>()), [hiddenState, uid]);
  const isVisible = useCallback((key: string) => !hidden.has(key), [hidden]);
  const setVisible = useCallback(
    async (key: string, on: boolean) => {
      const next = new Set(hidden);
      if (on) next.delete(key); else next.add(key);
      setHiddenState({ uid, set: next }); // 즉시 반영(서버 응답 전)
      if (mode === "unavailable") return;
      try { await userCalendarsRepo.setHidden(uid, next); } catch { /* 표시 설정 실패는 조용히 — 이 창에서는 반영됨 */ }
    },
    [uid, hidden, mode],
  );

  const add = useCallback(
    async (rawName: string, color?: CalendarColorKey): Promise<string> => {
      if (!uid) throw new Error("로그인이 필요합니다.");
      if (mode === "unavailable") throw new Error("캘린더 저장은 준비 중입니다(서버 설정 배포 전).");
      if (own.length >= USER_CALENDARS_MAX) throw new Error(`캘린더는 ${USER_CALENDARS_MAX}개까지 만들 수 있습니다.`);
      const r = normalizeCalendarName(rawName, calendars);
      if (!r.ok) throw new Error(r.error);
      const order = own.length ? Math.max(...own.map((c) => c.order)) + 1 : 0;
      return userCalendarsRepo.add(uid, r.name, color ?? nextCalendarColor(own), order);
    },
    [uid, mode, own, calendars],
  );

  const rename = useCallback(
    async (id: string, rawName: string) => {
      if (id === DEFAULT_CALENDAR_ID) throw new Error("기본 캘린더 이름은 바꿀 수 없습니다.");
      const r = normalizeCalendarName(rawName, calendars, id);
      if (!r.ok) throw new Error(r.error);
      await userCalendarsRepo.update(uid, id, { name: r.name });
    },
    [uid, calendars],
  );

  const recolor = useCallback(
    async (id: string, color: CalendarColorKey) => {
      if (id === DEFAULT_CALENDAR_ID) return;
      await userCalendarsRepo.update(uid, id, { color });
    },
    [uid],
  );

  const remove = useCallback(
    async (id: string): Promise<number> => {
      if (id === DEFAULT_CALENDAR_ID) throw new Error("기본 캘린더는 삭제할 수 없습니다.");
      const moved = await userCalendarsRepo.remove(uid, id);
      if (hidden.has(id)) void setVisible(id, true);
      return moved;
    },
    [uid, hidden, setVisible],
  );

  return { mode, calendars, own, calendarOf, isVisible, setVisible, add, rename, recolor, remove };
}

export type UserCalendarsApi = ReturnType<typeof useUserCalendars>;
